
import { supabase } from "@/integrations/supabase/client";
import { MarketplaceFilters } from "@/hooks/marketplace/types";

export const buildStrategiesQuery = async (
  filters: MarketplaceFilters,
  sortBy = 'performance',
  topPerformers = false
) => {
  try {
    // First, use a standard query without the join that's causing issues
    let query = supabase
      .from('strategy')
      .select('*')
      .eq('approved', true);
      
    // We now include both active and inactive strategies, but inactive will be shown as "coming soon"
    // Filter will only apply to active status if specifically requested in the filters
    
    // Apply filters
    if (filters.minInvestment !== 'all') {
      const minAmountValue = filters.minInvestment;
      
      // Handle different minimum investment filter options
      if (minAmountValue === '500' || minAmountValue === '1000' || minAmountValue === '2500') {
        // For these values, we want strategies with min_investment LESS than or equal to the value
        const maxAmount = parseInt(minAmountValue);
        query = query.lte('min_investment', maxAmount);
      } else if (minAmountValue === '2501') {
        // For 2501+, we want strategies with min_investment GREATER than or equal to 2501
        query = query.gte('min_investment', 2501);
      }
    }

    if (filters.riskLevel && filters.riskLevel.length > 0) {
      query = query.in('risk_level', filters.riskLevel.map(level => parseInt(level)));
    }

    if (filters.exchange && filters.exchange.length > 0) {
      query = query.in('exchange', filters.exchange);
    }

    if (filters.strategyTypes && filters.strategyTypes.length > 0) {
      const isPremium = filters.strategyTypes.includes('premium');
      const isFree = filters.strategyTypes.includes('free');
      
      if (isPremium && !isFree) {
        // Only show premium strategies - check for both 'premium' and 'pro' values
        query = query.or('plan.eq.premium,plan.eq.pro');
      } else if (isFree && !isPremium) {
        // Only show free strategies - use plan field
        query = query.eq('plan', 'free');
      }
      // If both or none are selected, don't filter
    }

    // Top performers logic - now using PNL ratio for sorting
    if (topPerformers) {
      query = query.order('expected_monthly_profit', { ascending: false }).limit(10);
    } else {
      // Apply sorting
      if (sortBy === 'performance') {
        // We'll sort by pnl_ratio in the frontend after fetching primary account performance data
        query = query.order('expected_monthly_profit', { ascending: false });
      } else if (sortBy === 'amount-high') {
        query = query.order('min_investment', { ascending: false });
      } else if (sortBy === 'amount-low') {
        query = query.order('min_investment', { ascending: true });
      } else if (sortBy === 'risk') {
        query = query.order('risk_level', { ascending: true });
      } else if (sortBy === 'followers') {
        // Handled after data fetch
        query = query;
      }
    }

    const { data, error } = await query;
    
    if (error) {
      throw error;
    }
    
    // Batch process all strategies to get total followers and AUM
    if (data && data.length > 0) {
      // Get all strategy IDs
      const strategyIds = data.map(strategy => strategy.id);
      
      // Batch fetch total followers
      const { data: followersData, error: followersError } = await supabase
        .rpc('get_strategy_total_followers_batch', { 
          strategy_ids: strategyIds 
        });
      
      // Batch fetch total AUM
      const { data: aumData, error: aumError } = await supabase
        .rpc('get_strategy_total_aum_batch', { 
          strategy_ids: strategyIds 
        });
      
      if (followersError) {
        console.error('Error fetching total followers in batch:', followersError);
      }
      
      if (aumError) {
        console.error('Error fetching total AUM in batch:', aumError);
      }
      
      // Create lookup maps - add type checking to ensure the data is an array
      const followersMap = followersData && Array.isArray(followersData) 
        ? Object.fromEntries(
            followersData.map(item => [item.strategy_id, item.total_followers])
          ) 
        : {};
      
      const aumMap = aumData && Array.isArray(aumData)
        ? Object.fromEntries(
            aumData.map(item => [item.strategy_id, item.total_aum])
          ) 
        : {};
      
      // Enhance strategies with the batch fetched data
      const enhancedStrategies = data.map(strategy => ({
        ...strategy,
        total_followers: followersMap[strategy.id] || 0,
        total_aum: aumMap[strategy.id] || 0
      }));
      
      // If sorting by followers, we need to sort again with our new data
      if (sortBy === 'followers' && !topPerformers) {
        enhancedStrategies.sort((a, b) => 
          (b.total_followers || 0) - (a.total_followers || 0)
        );
      }
      
      return { data: enhancedStrategies, error: null };
    }

    return { data, error };
  } catch (err) {
    console.error('Error building strategies query:', err);
    return { data: null, error: err };
  }
};
